import { useEffect, useRef, useState } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import client from "@/lib/api";
import { RARITIES, ITEM_TYPES, rarityMeta } from "@/lib/itemMeta";
import { imageUrl } from "@/pages/Items";
import { Plus, Edit3, Trash2, Save, X, Upload, Image as ImageIcon, ExternalLink } from "lucide-react";

// Slugify: lowercase, hyphenated, URL-safe. Order matters — strip invalid
// characters FIRST, then collapse whitespace+hyphens, then trim leading/trailing dashes.
const slugify = (s) =>
  (s || "")
    .toLowerCase()
    .replace(/[^a-z0-9\s-]+/g, "")  // drop anything not alphanum / space / hyphen
    .trim()
    .replace(/[\s-]+/g, "-")        // collapse whitespace and dashes into single hyphen
    .replace(/^-+|-+$/g, "");       // strip leading/trailing hyphens

const EMPTY_ITEM = {
  slug: "", name: "", image_url: "", rarity: "Common", type: "Weapon",
  description: "", stats: {}, source: "", production_chain: [], drops: [],
};

export default function ItemsTab() {
  const [params, setParams] = useSearchParams();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [reloadKey, setReloadKey] = useState(0);
  const [newMode, setNewMode] = useState(false);

  const editSlug = params.get("edit");

  // Load items whenever reloadKey changes
  useEffect(() => {
    client.get("/items")
      .then(r => { setItems(r.data); setLoading(false); })
      .catch(() => setLoading(false));
  }, [reloadKey]);

  // Derive editing target from URL (no state-sync effect needed)
  const editingItem = editSlug ? items.find(i => i.slug === editSlug) : null;
  const editing = newMode ? EMPTY_ITEM : editingItem;

  const startNew = () => setNewMode(true);
  const closeEditor = () => {
    setNewMode(false);
    if (editSlug) {
      const sp = new URLSearchParams(params);
      sp.delete("edit");
      setParams(sp);
    }
  };
  const onSaved = () => {
    closeEditor();
    setReloadKey(k => k + 1);
  };

  const onDelete = async (slug) => {
    if (!window.confirm(`Delete item "${slug}"?`)) return;
    await client.delete(`/items/${slug}`);
    setReloadKey(k => k + 1);
  };

  if (editing) {
    return <ItemEditor initial={editing} onCancel={closeEditor} onSaved={onSaved} />;
  }

  return (
    <div data-testid="items-tab">
      <div className="flex justify-between items-center mb-5 gap-3">
        <p className="text-sm text-[var(--t-text-dim)]">{items.length} items in the vault.</p>
        <button onClick={startNew} className="btn-primary !py-2 !px-4 !text-xs" data-testid="new-item-btn">
          <Plus className="w-4 h-4" /> New Item
        </button>
      </div>

      <AdminItemsBody loading={loading} items={items} onEdit={(slug) => setParams({ tab: "items", edit: slug })} onDelete={onDelete} />
    </div>
  );
}

function AdminItemsBody({ loading, items, onEdit, onDelete }) {
  if (loading) {
    return <div className="text-center py-10 font-mono text-xs tracking-[0.3em] text-[var(--t-blue-glow)] uppercase pulse-glow">// loading...</div>;
  }
  if (items.length === 0) {
    return <div className="panel p-10 text-center text-[var(--t-text-muted)] font-mono text-xs tracking-[0.2em] uppercase">// vault is empty · click new item to start</div>;
  }
  return (
    <div className="panel divide-y divide-[var(--t-border)]">
      {items.map(it => <AdminItemRow key={it.slug} item={it} onEdit={onEdit} onDelete={onDelete} />)}
    </div>
  );
}

function AdminItemRow({ item: it, onEdit, onDelete }) {
  const r = rarityMeta(it.rarity);
  const img = imageUrl(it.image_url);
  return (
    <div className="flex items-center gap-4 p-3 hover:bg-[rgba(58,160,255,0.04)]" data-testid={`admin-item-${it.slug}`}>
      <div className="w-14 h-14 flex-shrink-0 panel overflow-hidden" style={{ borderColor: r.color }}>
        {img
          ? <img src={img} alt="" className="w-full h-full object-cover" />
          : <div className="w-full h-full flex items-center justify-center"><ImageIcon className="w-5 h-5 text-[var(--t-text-muted)]" /></div>}
      </div>
      <div className="min-w-0 flex-1">
        <div className="flex items-center gap-2 flex-wrap">
          <span className={`inline-block px-2 py-0.5 text-[9px] font-mono tracking-[0.18em] uppercase border ${r.chip}`}>{it.rarity}</span>
          <span className="font-mono text-[10px] tracking-[0.2em] text-[var(--t-text-muted)] uppercase">{it.type}</span>
        </div>
        <div className="font-display text-base text-[var(--t-cyan)] uppercase mt-0.5 leading-tight">{it.name}</div>
        <div className="font-mono text-[10px] text-[var(--t-text-muted)] tracking-[0.15em] mt-0.5 truncate">/{it.slug}</div>
      </div>
      <div className="flex gap-1.5 flex-shrink-0">
        <a href={`/wiki/items/${it.slug}`} target="_blank" rel="noreferrer" className="btn-ghost !py-1.5 !px-2.5 !text-[10px]" data-testid={`view-item-${it.slug}`}><ExternalLink className="w-3 h-3" /></a>
        <button onClick={() => onEdit(it.slug)} className="btn-ghost !py-1.5 !px-2.5 !text-[10px]" data-testid={`edit-item-${it.slug}`}><Edit3 className="w-3 h-3" /></button>
        <button onClick={() => onDelete(it.slug)} className="btn-ghost !py-1.5 !px-2.5 !text-[10px] !border-[var(--t-red)] !text-[var(--t-red)]" data-testid={`delete-item-${it.slug}`}><Trash2 className="w-3 h-3" /></button>
      </div>
    </div>
  );
}

function ItemEditor({ initial, onCancel, onSaved }) {
  const isNew = !initial.id;
  const [form, setForm] = useState({
    slug: initial.slug || "",
    name: initial.name || "",
    image_url: initial.image_url || "",
    rarity: initial.rarity || "Common",
    type: initial.type || "Weapon",
    description: initial.description || "",
    source: initial.source || "",
    production_chain: (initial.production_chain || []).join(", "),
    stats: { ...(initial.stats || {}) },
  });
  const [statRows, setStatRows] = useState(() => {
    const entries = Object.entries(initial.stats || {});
    const seed = entries.length ? entries.map(([k, v]) => ({ k, v })) : [{ k: "", v: "" }];
    return seed.map((r, i) => ({ ...r, id: `stat-${Date.now()}-${i}` }));
  });
  const [dropRows, setDropRows] = useState(() => {
    const seed = (initial.drops || []).map(d => ({ slug: d.slug || "", weight: d.weight ?? 1 }));
    return (seed.length ? seed : [{ slug: "", weight: 1 }]).map((r, i) => ({ ...r, id: `drop-${Date.now()}-${i}` }));
  });
  const [allItems, setAllItems] = useState([]);
  useEffect(() => {
    client.get("/items").then(r => setAllItems(r.data || [])).catch(() => {});
  }, []);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState("");
  // True once the admin has manually edited the slug — stops auto-fill from
  // clobbering their choice. New items start un-touched so typing a name
  // generates the slug live.
  const [slugTouched, setSlugTouched] = useState(!isNew);
  const fileRef = useRef(null);

  const setField = (k, v) => {
    setForm(f => {
      const next = { ...f, [k]: v };
      // Auto-derive slug from name UNTIL the admin manually edits the slug.
      if (k === "name" && isNew && !slugTouched) next.slug = slugify(v);
      // If admin manually edits slug, lock it from further auto-derivation
      // and normalize what they typed (strip invalid chars, lowercase, hyphenate).
      if (k === "slug") {
        next.slug = slugify(v);
        setSlugTouched(true);
      }
      return next;
    });
  };

  const onUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setError(""); setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const r = await client.post("/uploads", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setField("image_url", r.data.url);
    } catch (err) {
      setError(err?.response?.data?.detail || "Upload failed");
    } finally {
      setUploading(false);
      if (fileRef.current) fileRef.current.value = "";
    }
  };

  const updateStatRow = (i, field, value) => {
    setStatRows(rows => rows.map((row, idx) => idx === i ? { ...row, [field]: value } : row));
  };
  const addStatRow = () => setStatRows(rows => [...rows, { id: `stat-${Date.now()}-${rows.length}`, k: "", v: "" }]);
  const removeStatRow = (i) => setStatRows(rows => rows.filter((_, idx) => idx !== i));

  const updateDropRow = (i, field, value) => {
    setDropRows(rows => rows.map((row, idx) => idx === i ? { ...row, [field]: value } : row));
  };
  const addDropRow = () => setDropRows(rows => [...rows, { id: `drop-${Date.now()}-${rows.length}`, slug: "", weight: 1 }]);
  const removeDropRow = (i) => setDropRows(rows => rows.filter((_, idx) => idx !== i));

  const cleanDrops = () =>
    dropRows
      .map(r => ({ slug: slugify((r.slug || "").trim()), weight: Math.max(0, Number(r.weight) || 0) }))
      .filter(d => d.slug && d.weight > 0);

  const totalDropWeight = cleanDrops().reduce((s, d) => s + d.weight, 0) || 0;

  const save = async () => {
    setError("");
    if (!form.name.trim()) { setError("Name is required."); return; }
    const finalSlug = slugify(form.slug || form.name);
    if (!finalSlug) { setError("Slug is required (couldn't derive one from the name)."); return; }
    if (!/^[a-z0-9]+(?:-[a-z0-9]+)*$/.test(finalSlug)) {
      setError("Slug must be lowercase letters, numbers, and hyphens only.");
      return;
    }
    const payload = {
      ...form,
      slug: finalSlug,
      stats: Object.fromEntries(statRows.filter(r => r.k.trim()).map(r => [r.k.trim(), r.v])),
      production_chain: (form.production_chain || "")
        .split(",")
        .map(s => slugify(s.trim()))
        .filter(Boolean),
      drops: form.type === "Case" ? cleanDrops() : [],
    };
    setSaving(true);
    try {
      await client.post("/items", payload);
      onSaved();
    } catch (err) {
      setError(err?.response?.data?.detail || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  const r = rarityMeta(form.rarity);
  const previewImg = imageUrl(form.image_url);

  return (
    <div data-testid="item-editor">
      <div className="flex items-center justify-between mb-5 gap-3">
        <h2 className="font-display text-xl md:text-2xl text-[var(--t-text)] uppercase">{isNew ? "New Item" : "Edit Item"}</h2>
        <div className="flex gap-2">
          <button onClick={onCancel} className="btn-ghost !py-2 !px-3 !text-xs" data-testid="item-cancel"><X className="w-3.5 h-3.5" /> Cancel</button>
          <button onClick={save} disabled={saving} className="btn-primary !py-2 !px-4 !text-xs" data-testid="item-save"><Save className="w-3.5 h-3.5" /> {saving ? "Saving..." : "Save"}</button>
        </div>
      </div>

      {error && <div className="panel p-3 mb-4 text-sm text-[var(--t-red)] font-mono" data-testid="item-error">{error}</div>}

      <div className="grid md:grid-cols-[280px_1fr] gap-6">
        {/* IMAGE COLUMN */}
        <div className="space-y-3">
          <div className="aspect-square panel overflow-hidden" style={{ borderColor: r.color, boxShadow: `0 0 24px ${r.glow}` }}>
            {previewImg ? (
              <img src={previewImg} alt="" className="w-full h-full object-cover" />
            ) : (
              <div className="w-full h-full flex items-center justify-center bg-[#050912]">
                <ImageIcon className="w-12 h-12 text-[var(--t-text-muted)]" />
              </div>
            )}
          </div>
          <button onClick={() => fileRef.current?.click()} disabled={uploading} className="btn-primary w-full !justify-center !py-2 !text-xs" data-testid="item-upload-btn">
            <Upload className="w-3.5 h-3.5" /> {uploading ? "Uploading..." : "Upload Image"}
          </button>
          <input ref={fileRef} type="file" accept="image/*" onChange={onUpload} className="hidden" data-testid="item-file-input" />
          <input className="field !text-xs" placeholder="…or paste image URL" value={form.image_url} onChange={(e) => setField("image_url", e.target.value)} data-testid="item-image-url" />
          {form.image_url && (
            <button onClick={() => setField("image_url", "")} className="text-[10px] font-mono tracking-[0.2em] text-[var(--t-text-muted)] uppercase hover:text-[var(--t-red)]" data-testid="item-clear-image">Remove image</button>
          )}
        </div>

        {/* FIELDS COLUMN */}
        <div className="space-y-4">
          <div className="grid md:grid-cols-2 gap-3">
            <div>
              <label className="font-mono text-[10px] tracking-[0.25em] text-[var(--t-blue-glow)] uppercase block mb-1.5">Name *</label>
              <input className="field" value={form.name} onChange={(e) => setField("name", e.target.value)} placeholder="e.g. Blade of the Architect" data-testid="item-name" />
            </div>
            <div>
              <label className="font-mono text-[10px] tracking-[0.25em] text-[var(--t-blue-glow)] uppercase block mb-1.5">Slug</label>
              <input className="field font-mono text-sm" value={form.slug} onChange={(e) => setField("slug", e.target.value)} placeholder="blade-of-the-architect" disabled={!isNew} data-testid="item-slug" />
            </div>
          </div>

          <div className="grid md:grid-cols-2 gap-3">
            <div>
              <label className="font-mono text-[10px] tracking-[0.25em] text-[var(--t-blue-glow)] uppercase block mb-1.5">Rarity</label>
              <select className="field" value={form.rarity} onChange={(e) => setField("rarity", e.target.value)} data-testid="item-rarity">
                {RARITIES.map(r => <option key={r.key} value={r.key}>{r.key}</option>)}
              </select>
            </div>
            <div>
              <label className="font-mono text-[10px] tracking-[0.25em] text-[var(--t-blue-glow)] uppercase block mb-1.5">Type</label>
              <select className="field" value={form.type} onChange={(e) => setField("type", e.target.value)} data-testid="item-type">
                {ITEM_TYPES.map(t => <option key={t} value={t}>{t}</option>)}
              </select>
            </div>
          </div>

          <div>
            <label className="font-mono text-[10px] tracking-[0.25em] text-[var(--t-blue-glow)] uppercase block mb-1.5">Description (markdown)</label>
            <textarea className="field" rows={6} value={form.description} onChange={(e) => setField("description", e.target.value)} placeholder="Lore, mechanics, tactical notes..." data-testid="item-description" />
          </div>

          <div>
            <label className="font-mono text-[10px] tracking-[0.25em] text-[var(--t-blue-glow)] uppercase block mb-1.5">Stats</label>
            <div className="panel p-3 space-y-2">
              {statRows.map((row, i) => (
                <div key={row.id} className="flex gap-2" data-testid={`stat-row-${i}`}>
                  <input className="field !py-1.5 !text-sm flex-1" placeholder="Attack" value={row.k} onChange={(e) => updateStatRow(i, "k", e.target.value)} />
                  <input className="field !py-1.5 !text-sm flex-1" placeholder="+25" value={row.v} onChange={(e) => updateStatRow(i, "v", e.target.value)} />
                  <button onClick={() => removeStatRow(i)} className="btn-ghost !py-1.5 !px-2 !text-[10px]" aria-label="Remove stat"><X className="w-3 h-3" /></button>
                </div>
              ))}
              <button onClick={addStatRow} className="btn-ghost !py-1.5 !px-3 !text-[10px]" data-testid="add-stat-row"><Plus className="w-3 h-3" /> Add stat</button>
            </div>
          </div>

          <div>
            <label className="font-mono text-[10px] tracking-[0.25em] text-[var(--t-blue-glow)] uppercase block mb-1.5">Source (markdown)</label>
            <textarea className="field" rows={3} value={form.source} onChange={(e) => setField("source", e.target.value)} placeholder="e.g. Drops from Mythic cases. Weekly leaderboard top 10." data-testid="item-source-input" />
          </div>
          <div>
            <label className="block font-mono text-xs tracking-[0.2em] text-[var(--t-blue-glow)] uppercase mb-1.5">Production Chain</label>
            <input
              className="field"
              value={form.production_chain}
              onChange={(e) => setField("production_chain", e.target.value)}
              placeholder="e.g. iron-ore, coal (comma-separated item slugs)"
              data-testid="item-production-chain-input"
            />
            <p className="font-mono text-[10px] tracking-[0.15em] text-[var(--t-text-muted)] mt-1.5">
              Comma-separated slugs of items required to craft this item. Each becomes a clickable link on the item page.
            </p>
          </div>

          {form.type === "Case" && (
            <div data-testid="case-drops-editor">
              <label className="block font-mono text-xs tracking-[0.2em] text-[var(--t-blue-glow)] uppercase mb-1.5">
                Case Drops · <span className="text-[var(--t-text-muted)]">weighted pool</span>
              </label>
              <div className="panel p-3 space-y-2">
                {dropRows.map((row, i) => {
                  const itemMatch = allItems.find(a => a.slug === slugify(row.slug || ""));
                  const pct = totalDropWeight > 0
                    ? ((Math.max(0, Number(row.weight) || 0) / totalDropWeight) * 100)
                    : 0;
                  return (
                    <div key={row.id} className="flex flex-wrap items-center gap-2" data-testid={`drop-row-${i}`}>
                      <input
                        list={`drop-slug-list-${i}`}
                        className="field !py-1.5 !text-sm flex-1 min-w-[180px] font-mono"
                        placeholder="item-slug (e.g. combat-knife)"
                        value={row.slug}
                        onChange={(e) => updateDropRow(i, "slug", e.target.value)}
                      />
                      <datalist id={`drop-slug-list-${i}`}>
                        {allItems.filter(a => a.type !== "Case").map(a => (
                          <option key={a.slug} value={a.slug}>{a.name}</option>
                        ))}
                      </datalist>
                      <input
                        type="number"
                        min="0"
                        step="1"
                        className="field !py-1.5 !text-sm w-20"
                        placeholder="weight"
                        value={row.weight}
                        onChange={(e) => updateDropRow(i, "weight", e.target.value)}
                      />
                      <span className="font-mono text-[11px] text-[var(--t-cyan)] min-w-[44px] text-right">
                        {pct.toFixed(1)}%
                      </span>
                      <span className="font-mono text-[10px] text-[var(--t-text-muted)] flex-1 min-w-[80px] truncate">
                        {itemMatch ? `→ ${itemMatch.name}` : row.slug ? "→ unknown slug" : ""}
                      </span>
                      <button onClick={() => removeDropRow(i)} className="btn-ghost !py-1.5 !px-2 !text-[10px]" aria-label="Remove drop">
                        <X className="w-3 h-3" />
                      </button>
                    </div>
                  );
                })}
                <div className="flex items-center justify-between pt-1">
                  <button onClick={addDropRow} className="btn-ghost !py-1.5 !px-3 !text-[10px]" data-testid="add-drop-row">
                    <Plus className="w-3 h-3" /> Add drop
                  </button>
                  <span className="font-mono text-[10px] text-[var(--t-text-muted)] uppercase tracking-[0.18em]">
                    Total weight: <span className="text-[var(--t-cyan)]">{totalDropWeight}</span>
                  </span>
                </div>
              </div>
              <p className="font-mono text-[10px] tracking-[0.15em] text-[var(--t-text-muted)] mt-1.5">
                Add weighted item slugs. % shown is each item&apos;s chance relative to total weight. Only saved for items with type = Case.
              </p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
